import type { TFunction } from "i18next";
import {
  type ContractDto,
  type Public,
  FoaPart,
  NoCompetitionCondition,
  ProcedureType,
  Regulation,
  NegotiationImpossibleCase,
  WindingUpCase,
} from "../../../../../autogen/bff-api";
import { isOrdinaryEeaTier } from "./karens";

export const isWithoutCompetition = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType === ProcedureType.WithoutCompetition;

// Mirrors FieldKeys.WithoutCompetitionAtEeaTier in the BFF: FOA del III and forsyningsforskriften del II.
export const offersWithoutCompetition = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => isOrdinaryEeaTier(regulation, foaPart);

const ALL_CONDITIONS: readonly NoCompetitionCondition[] = [
  NoCompetitionCondition.NegotiatedProcedureImpossible,
  NoCompetitionCondition.UniqueArtisticWork,
  NoCompetitionCondition.TechnicalReasons,
  NoCompetitionCondition.ExclusiveRights,
  NoCompetitionCondition.AdditionalDeliveries,
  NoCompetitionCondition.RepetitionOfSimilarWorks,
  NoCompetitionCondition.CommodityMarket,
  NoCompetitionCondition.BargainPurchase,
  NoCompetitionCondition.WindingUpTerms,
  NoCompetitionCondition.DesignContestWinner,
];

// Forsyningsforskriften § 9-3 f has no counterpart in FOA § 13-4.
export const noCompetitionConditionsFor = (
  regulation: Regulation | null | undefined,
): readonly NoCompetitionCondition[] =>
  regulation === Regulation.Forsyningsforskriften
    ? ALL_CONDITIONS
    : ALL_CONDITIONS.filter((condition) => condition !== NoCompetitionCondition.BargainPurchase);

export const extendsOriginalContract = (condition: NoCompetitionCondition | null | undefined): boolean =>
  condition === NoCompetitionCondition.AdditionalDeliveries ||
  condition === NoCompetitionCondition.RepetitionOfSimilarWorks;

type Paragraph = { letter: string; number?: string };

const PARAGRAPHS: Record<NoCompetitionCondition, { foa: Paragraph; utilities: Paragraph }> = {
  [NoCompetitionCondition.NegotiatedProcedureImpossible]: { foa: { letter: "a" }, utilities: { letter: "a" } },
  [NoCompetitionCondition.UniqueArtisticWork]: {
    foa: { letter: "b", number: "1" },
    utilities: { letter: "b", number: "1" },
  },
  [NoCompetitionCondition.TechnicalReasons]: {
    foa: { letter: "b", number: "2" },
    utilities: { letter: "b", number: "2" },
  },
  [NoCompetitionCondition.ExclusiveRights]: {
    foa: { letter: "b", number: "3" },
    utilities: { letter: "b", number: "3" },
  },
  [NoCompetitionCondition.AdditionalDeliveries]: { foa: { letter: "c" }, utilities: { letter: "c" } },
  [NoCompetitionCondition.RepetitionOfSimilarWorks]: { foa: { letter: "d" }, utilities: { letter: "d" } },
  [NoCompetitionCondition.CommodityMarket]: { foa: { letter: "e" }, utilities: { letter: "e" } },
  [NoCompetitionCondition.BargainPurchase]: { foa: { letter: "f" }, utilities: { letter: "f" } },
  [NoCompetitionCondition.WindingUpTerms]: { foa: { letter: "f" }, utilities: { letter: "g" } },
  [NoCompetitionCondition.DesignContestWinner]: { foa: { letter: "g" }, utilities: { letter: "h" } },
};

// Mirrors EventContext.Public.followsDesignContestBelowEeaTier in the BFF: FOA § 31-2 (2), jf. § 8-17 (5) a.
export const followsDesignContestBelowEeaTier = (ctx: Public | null | undefined): boolean => {
  const link = ctx?.designContestFollowUp;
  if (!ctx || !link?.contestBelowEeaTier) return false;
  if (ctx.regulation !== Regulation.Foa || ctx.foaPart !== FoaPart.PartIi) return false;
  if (ctx.procedureType === ProcedureType.WithoutCompetition) {
    return ctx.noCompetitionJustification?.condition === NoCompetitionCondition.DesignContestWinner;
  }
  return (
    ctx.procedureType === ProcedureType.NegotiatedWithoutPriorCall &&
    ctx.negotiationWithoutNotice?.case === NegotiationImpossibleCase.DesignContestWinners &&
    ctx.negotiationWithoutNotice.precedingEventId === link.contestId
  );
};

// Mirrors NoCompetitionCondition.provision in the BFF, which the protocol prints in full.
export const noCompetitionProvision = (
  condition: NoCompetitionCondition,
  regulation: Regulation | null | undefined,
  t: TFunction,
  contestBelowEeaTier = false,
): string => {
  if (condition === NoCompetitionCondition.DesignContestWinner && contestBelowEeaTier) {
    return t("FOA § 8-17 (5) (a)");
  }
  if (regulation === Regulation.Forsyningsforskriften) {
    const { letter, number } = PARAGRAPHS[condition].utilities;
    return number
      ? t("forsyningsforskriften § 9-3 ({{letter}})({{number}})", { letter, number })
      : t("forsyningsforskriften § 9-3 ({{letter}})", { letter });
  }
  const { letter, number } = PARAGRAPHS[condition].foa;
  return number
    ? t("FOA § 13-4 ({{letter}})({{number}})", { letter, number })
    : t("FOA § 13-4 ({{letter}})", { letter });
};

const NEGOTIATION_CASE_LETTERS: Record<NegotiationImpossibleCase, { foa: string; utilities: string | null }> = {
  [NegotiationImpossibleCase.OnlyUnacceptableTenders]: { foa: "a", utilities: null },
  [NegotiationImpossibleCase.NoSuitableTenders]: { foa: "b", utilities: "a" },
  [NegotiationImpossibleCase.ResearchAndDevelopment]: { foa: "c", utilities: "b" },
  [NegotiationImpossibleCase.DesignContestWinners]: { foa: "d", utilities: "c" },
  [NegotiationImpossibleCase.ExtremeUrgency]: { foa: "e", utilities: "d" },
  [NegotiationImpossibleCase.CoverPurchase]: { foa: "f", utilities: "e" },
};

// FOA § 13-3 a–f; forsyningsforskriften § 9-2 a covers only unacceptable tenders too, so it has no own case.
export const negotiationImpossibleCasesFor = (
  regulation: Regulation | null | undefined,
): NegotiationImpossibleCase[] =>
  (Object.keys(NEGOTIATION_CASE_LETTERS) as NegotiationImpossibleCase[]).filter(
    (option) => regulation !== Regulation.Forsyningsforskriften || NEGOTIATION_CASE_LETTERS[option].utilities != null,
  );

export const negotiationCaseProvision = (
  option: NegotiationImpossibleCase,
  regulation: Regulation | null | undefined,
  t: TFunction,
  contestBelowEeaTier = false,
): string => {
  if (option === NegotiationImpossibleCase.DesignContestWinners && contestBelowEeaTier) {
    return t("FOA § 8-17 (5) (a)");
  }
  const utilitiesLetter = NEGOTIATION_CASE_LETTERS[option].utilities;
  return regulation === Regulation.Forsyningsforskriften && utilitiesLetter != null
    ? t("forsyningsforskriften § 9-2 ({{letter}})", { letter: utilitiesLetter })
    : t("FOA § 13-3 ({{letter}})", { letter: NEGOTIATION_CASE_LETTERS[option].foa });
};

export const WINDING_UP_CASES: readonly WindingUpCase[] = [
  WindingUpCase.SupplierStoppedTrading,
  WindingUpCase.InsolvencyAdministrator,
];

const ORIGINAL_CONTRACT_YEARS = 3;

export type OriginalContractDate = { date: string; countedFrom: "award" | "start" | "registration" };

export const originalContractDate = (contract: ContractDto): OriginalContractDate => {
  if (contract.dataFields?.awardDate) return { date: contract.dataFields.awardDate, countedFrom: "award" };
  if (contract.dataFields?.startDate) return { date: contract.dataFields.startDate, countedFrom: "start" };
  return { date: contract.createdAt, countedFrom: "registration" };
};

// FOA § 13-4 c and d: additional deliveries normally within three years, a repetition at the latest
// three years after the original contract.
export const isOriginalContractOverThreeYears = (date: string, today: Date): boolean => {
  const limit = new Date(today);
  limit.setFullYear(limit.getFullYear() - ORIGINAL_CONTRACT_YEARS);
  return new Date(date) < limit;
};
